import type { IHeatmapFilterComponentType } from '~/modules/heatmap/types';
import type { ChoiceOptionItem } from '~/presentation/ui';

import { useHeatmapFilterContext, useHeatmapFilterConfig } from '~/modules/heatmap';
import { EHeatmapFilterType, ETrafficSourceType } from '~/modules/heatmap/types';
import { GSelectMultiple } from '~/presentation/common';
import { GCardFilter } from '~/presentation/ui';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

const TRAFFIC_SOURCE_OPTIONS: ChoiceOptionItem<ETrafficSourceType>[] = [
  { value: ETrafficSourceType.DIRECT, label: 'Direct' },
  { value: ETrafficSourceType.EMAIL, label: 'Email' },
  { value: ETrafficSourceType.REFERRAL, label: 'Referral' },
  { value: ETrafficSourceType.ORGANIC_SOCIAL, label: 'Organic social' },
  { value: ETrafficSourceType.ORGANIC_SEARCH, label: 'Organic search' },
  { value: ETrafficSourceType.PAID_SOCIAL, label: 'Paid social' },
  { value: ETrafficSourceType.PAID_SEARCH, label: 'Paid search' },
  { value: ETrafficSourceType.SMS, label: 'SMS' },
];

export const FilterTrafficSource: IHeatmapFilterComponentType<
  EHeatmapFilterType.TRAFFIC_SOURCE
> = ({ data }) => {
  const removeFilter = useHeatmapFilterContext((s) => s.removeFilter);
  const setDraftFilter = useHeatmapFilterContext((s) => s.setDraftFilter);

  const { t } = useTranslation();
  const { configInfoBy } = useHeatmapFilterConfig();

  const configInfo = configInfoBy(EHeatmapFilterType.TRAFFIC_SOURCE);

  const selectedValues = useMemo(() => data.value ?? [], [data.value]);

  const options = useMemo(
    () =>
      TRAFFIC_SOURCE_OPTIONS.map((item) => ({
        ...item,
        selected: selectedValues.includes(item.value),
      })),
    [selectedValues],
  );

  const handleSelect = (value: string) => {
    const selectedValue = value as ETrafficSourceType;

    const newValues = selectedValues.includes(selectedValue)
      ? selectedValues.filter((item) => item !== selectedValue)
      : [...selectedValues, selectedValue];

    setDraftFilter(EHeatmapFilterType.TRAFFIC_SOURCE, { value: newValues });
  };

  return (
    <GCardFilter
      title={configInfo.label}
      icon={configInfo.icon}
      onRemove={() => removeFilter(EHeatmapFilterType.TRAFFIC_SOURCE)}
    >
      <GSelectMultiple
        placeholder={t('Select')}
        options={options}
        selected={selectedValues}
        selectedAllContent={t('All sources')}
        onSelect={handleSelect}
      />
    </GCardFilter>
  );
};
